import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { styled } from '@mui/system';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { ArrowRight } from 'lucide-react';
import { useTranslate } from 'hooks/useTranslate';
import { getLanguage } from 'translation';
import { getLocalizedPath } from 'helpers/getLocale';
import Image from 'assets/images/get_consultation.avif';

const Background = styled('section')(({ theme }) => ({
  backgroundColor: theme.palette.backgroundNeutral.main
}));

const Wrapper = styled('div')(({ theme }) => ({
  padding: '0 100px 80px',
  boxSizing: 'border-box',
  ...theme.wrapper,
  [theme.breakpoints.down('lg')]: {
    padding: '0 50px 50px'
  },
  [theme.breakpoints.down('md')]: {
    padding: '0 30px 30px'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '0 20px 30px'
  }
}));

const Banner = styled(Button)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  position: 'relative',
  cursor: 'pointer',
  overflow: 'hidden',
  borderRadius: 15,
  padding: 0,
  marginBottom: 40,
  '& img': {
    transition: 'all 0.2s',
    width: '100%',
    objectFit: 'cover'
  },
  '&:hover': {
    backgroundColor: 'transparent',
    '& img': {
      transform: 'scale(1.05)'
    }
  },
  [theme.breakpoints.down('md')]: {
    marginBottom: 30,
    '& img': {
      height: 185
    }
  }
}));

const AbsoluteFlex = styled('div')(() => ({
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  justifyContent: 'flex-end'
}));

const Label = styled(Typography)(({ theme }) => ({
  fontSize: 21,
  fontWeight: 600,
  color: theme.palette.black.main,
  backgroundColor: theme.palette.secondary.main,
  padding: 18,
  textAlign: 'center',
  borderRadius: 15,
  display: 'flex',
  alignSelf: 'center',
  flex: 1,
  flexDirection: 'column',
  maxWidth: 480,
  marginRight: 30,
  [theme.breakpoints.down('md')]: {
    margin: 16,
    fontSize: 16
  }
}));

const SeeAllButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  marginLeft: 'auto',
  borderRadius: 25,
  textTransform: 'none',
  padding: '12px 24px',
  fontSize: 16,
  // 500 matches the rest of the CTA family (Onboarding pills, /pro
  // PrimaryActionButton, Assessment Welcome buttons). Was 600.
  fontWeight: 500,
  '& .MuiButton-endIcon': {
    transition: 'transform 0.2s ease'
  },
  '&:hover .MuiButton-endIcon': {
    transform: 'translateX(4px)'
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: '10px 20px',
    fontSize: 14
  }
}));

const GetConsultation = ({ texts }) => {
  const tHome = useTranslate('Home');
  const tProfession = useTranslate('Profession');
  const lang = getLanguage();
  const navigate = useNavigate();
  // The consultants feature is off on some brands (e.g. jobalto); when it is,
  // the underlying /consultants API returns "Service unavailable" and clicking
  // through goes nowhere useful, so don't render the section at all.
  const consultantsEnabled = useSelector((state) => state.features.flags.consultants === true);

  // i18next returns the full key path (e.g. 'Home.ConsultationLabel') when a
  // translation is missing — not the bare key — so the old `!== 'ConsultationLabel'`
  // comparison silently leaked the raw key onto the page. Treat any value
  // ending in the key name as a miss.
  const homeLabel = tHome('ConsultationLabel');
  const label =
    homeLabel && !homeLabel.endsWith('ConsultationLabel')
      ? homeLabel
      : tProfession('GetConsultation');

  const handleConsultantsClick = React.useCallback(() => {
    navigate(getLocalizedPath('/counselors'));
  }, [navigate]);

  const handleProfessionsClick = React.useCallback(() => {
    navigate(getLocalizedPath('/professions'));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [navigate]);

  if (!consultantsEnabled) return null;

  return (
    <Background>
      <Wrapper>
        <Banner onClick={handleConsultantsClick} tabIndex={0}>
          <img src={Image} alt={label} />
          <AbsoluteFlex>
            <Label>
              <span>{label}</span>
              <span>{tProfession('GetConsultation2')}</span>
            </Label>
          </AbsoluteFlex>
        </Banner>
        <SeeAllButton
          variant="contained"
          color="primary"
          onClick={handleProfessionsClick}
          endIcon={<ArrowRight size={18} />}
          aria-label={texts?.occupations_button?.[lang]}>
          {texts?.occupations_button?.[lang]}
        </SeeAllButton>
      </Wrapper>
    </Background>
  );
};

export default GetConsultation;
